import React from 'react';
import { Box } from '@mui/material';
import Navbar from '../Navbar';
import Sidebar from '../SideBar/Sidebar';
import { useAuth } from '../../hooks/useAuth';
import {
  useSidebarRail,
  SIDEBAR_RAIL_COLLAPSED_PX,
  SIDEBAR_RAIL_EXPANDED_PX,
  SIDEBAR_XS_WIDTH_PX,
} from '../../context/SidebarRailContext';

const NAVBAR_BASE_MARGIN_PX = 80;
const IMPERSONATION_BANNER_PX = 40;

const PageLayout = ({ children, sx = {}, ...rest }) => {
  const { railExpanded } = useSidebarRail();
  const { impersonating } = useAuth();
  const smOffset = railExpanded ? SIDEBAR_RAIL_EXPANDED_PX : SIDEBAR_RAIL_COLLAPSED_PX;
  const mainMarginTop = impersonating
    ? `${NAVBAR_BASE_MARGIN_PX + IMPERSONATION_BANNER_PX}px`
    : `${NAVBAR_BASE_MARGIN_PX}px`;

  // Navbar + Sidebar are position:fixed (out of flex flow), so this is the only flex child.
  // Explicit width + marginLeft prevents the column from staying full viewport width and sitting under the sidebar.
  const contentStyles = {
    flex: '0 0 auto',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    marginLeft: { xs: `${SIDEBAR_XS_WIDTH_PX}px`, sm: `${smOffset}px` },
    width: {
      xs: `calc(100% - ${SIDEBAR_XS_WIDTH_PX}px)`,
      sm: `calc(100% - ${smOffset}px)`,
    },
    maxWidth: {
      xs: `calc(100% - ${SIDEBAR_XS_WIDTH_PX}px)`,
      sm: `calc(100% - ${smOffset}px)`,
    },
    marginTop: mainMarginTop,
    minWidth: 0,
    overflowX: 'hidden',
    transition: (theme) =>
      theme.transitions.create(['margin-left', 'width', 'max-width'], {
        duration: 220,
        easing: theme.transitions.easing.easeInOut,
      }),
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }} {...rest}>
      <Navbar />
      <Sidebar />
      <Box sx={{ ...contentStyles, ...sx }}>{children}</Box>
    </Box>
  );
};

/**
 * Standard page layout with Navbar + Sidebar + content area.
 * Main body margin tracks sidebar width (sm+: 72px collapsed / 250px expanded).
 * Sidebar rail open/closed state is provided at the app root (SidebarRailProvider) so it persists across routes.
 */
export default PageLayout;
